import React, { useState, useCallback, useEffect } from "react";
import {
  Upload,
  File,
  Loader2,
  Download,
  CheckCircle,
  AlertCircle,
  XCircle,
} from "lucide-react";
import { getSchools } from "components/common/utils";

interface Props {
  handleFileUpload: (file: File, schoolName: string) => void;
  uploadStatus: number;
}

const FileUpload: React.FC<Props> = ({ handleFileUpload, uploadStatus }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [schoolName, setSchoolName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // États pour la gestion des écoles
  const [schoolList, setSchoolList] = useState<string[]>([]);
  const [loadingSchools, setLoadingSchools] = useState(false);

  // Fonction pour charger les écoles
  const loadSchools = async () => {
    setLoadingSchools(true);
    try {
      const schools = await getSchools();
      setSchoolList(schools);
    } catch (error) {
      console.error("Erreur lors du chargement des écoles:", error);
      setSchoolList([]);
    } finally {
      setLoadingSchools(false);
    }
  };

  // Charger les écoles au montage du composant
  useEffect(() => {
    loadSchools();
  }, []);

  // Vider le champ de fichier après un upload réussi
  useEffect(() => {
    if (uploadStatus === 200 || uploadStatus === 400 || uploadStatus === 409) {
      setSelectedFile(null);
      // Réinitialiser l'input file
      const fileInput = document.querySelector(
        'input[type="file"]',
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    }
  }, [uploadStatus]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files?.[0]) {
      if (
        files[0].type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        files[0].type === "application/vnd.ms-excel"
      ) {
        setSelectedFile(files[0]);
      }
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (selectedFile && schoolName.trim()) {
      setIsUploading(true);
      try {
        await handleFileUpload(selectedFile, schoolName.trim());
      } finally {
        setIsUploading(false);
      }
    }
  };

  const downloadTemplate = () => {
    // Créer un lien pour télécharger le template
    const link = document.createElement("a");
    link.href = "/assets/template_ajout_eleves_excel.xlsx";
    link.download = "template_ajout_eleves_excel.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fonction pour obtenir le statut de l'upload
  const getUploadStatusInfo = () => {
    switch (uploadStatus) {
      case 0:
        return { icon: null, message: "", color: "" };
      case 200:
        return {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />,
          message:
            "Import réussi ! Vous recevrez un mail de rapport de création.",
          color: "bg-green-50 border-green-200 text-green-800",
        };
      case 400:
      case 409:
        return {
          icon: <AlertCircle className="w-5 h-5 text-yellow-500" />,
          message:
            "Import partiel. Vous recevrez un mail de rapport de création.",
          color: "bg-yellow-50 border-yellow-200 text-yellow-800",
        };
      case 500:
        return {
          icon: <XCircle className="w-5 h-5 text-red-500" />,
          message: "Erreur lors de l'import. Veuillez vérifier votre fichier.",
          color: "bg-red-50 border-red-200 text-red-800",
        };
      default:
        return { icon: null, message: "", color: "" };
    }
  };

  const statusInfo = getUploadStatusInfo();

  return (
    <div className="w-full max-w-2xl mx-auto p-4 md:p-6">
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 md:p-8">
        <h2 className="text-xl md:text-2xl font-bold text-center text-gray-800 mb-6">
          Création de compte via Excel
        </h2>

        {/* Feedback de statut */}
        {uploadStatus > 0 && (
          <div
            className={`mb-6 p-4 rounded-lg border ${statusInfo.color} flex items-center gap-3`}
          >
            {statusInfo.icon}
            <p className="text-sm md:text-base font-medium">
              {statusInfo.message}
            </p>
          </div>
        )}

        {/* Bouton de téléchargement du template */}
        <div className="mb-6 p-4 md:p-6 bg-[#131CC9]/5 rounded-lg border border-[#131CC9]/20">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-sm md:text-base font-semibold text-[#131CC9] mb-1">
                Template Excel
              </h3>
              <p className="text-xs md:text-sm text-[#131CC9]/70">
                Téléchargez le template pour voir le format attendu
              </p>
            </div>
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-[#131CC9] hover:bg-[#0f1699] text-white text-sm md:text-base font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              <Download className="w-4 h-4 md:w-5 md:h-5" />
              Télécharger
            </button>
          </div>
        </div>

        {/* Champ pour le nom de l'école */}
        <div className="mb-6">
          <label
            htmlFor="schoolName"
            className="block text-sm md:text-base font-semibold text-gray-700 mb-2"
          >
            Nom de l'école
          </label>
          {loadingSchools ? (
            <div className="w-full border border-gray-300 rounded-lg px-4 py-3 bg-gray-50 flex items-center gap-3">
              <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin text-[#131CC9]" />
              <span className="text-sm md:text-base text-gray-600">
                Chargement des écoles...
              </span>
            </div>
          ) : (
            <select
              id="schoolName"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#131CC9] focus:border-[#131CC9] bg-white text-sm md:text-base transition-colors duration-200"
              disabled={isUploading}
            >
              <option value="">Sélectionnez une école</option>
              {schoolList.map((school) => (
                <option key={school} value={school}>
                  {school}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Zone de drop */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-lg p-6 md:p-8 transition-all duration-200 ease-in-out
            ${
              isDragging
                ? "border-[#131CC9] bg-[#131CC9]/5"
                : selectedFile
                ? "border-[#E3B505] bg-[#E3B505]/5"
                : "border-gray-300 hover:border-[#131CC9] hover:bg-[#131CC9]/2"
            }
            ${isUploading ? "opacity-50 pointer-events-none" : ""}
          `}
        >
          <input
            type="file"
            onChange={handleFileChange}
            accept=".xlsx,.xls"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={isUploading}
          />
          <div className="flex flex-col items-center justify-center text-center space-y-3 md:space-y-4">
            {selectedFile ? (
              <>
                <File className="w-10 h-10 md:w-12 md:h-12 text-[#E3B505]" />
                <p className="text-sm md:text-base font-semibold text-gray-900">
                  {selectedFile.name}
                </p>
                <p className="text-xs md:text-sm text-gray-500">
                  Cliquez ou glissez pour changer
                </p>
              </>
            ) : (
              <>
                <Upload className="w-10 h-10 md:w-12 md:h-12 text-[#131CC9]" />
                <p className="text-sm md:text-base font-semibold text-gray-900">
                  Glissez votre fichier Excel
                </p>
                <p className="text-xs md:text-sm text-gray-500">
                  ou cliquez pour sélectionner
                </p>
              </>
            )}
          </div>
        </div>

        {/* Bouton de soumission */}
        <button
          onClick={handleSubmit}
          disabled={!selectedFile || !schoolName.trim() || isUploading}
          className={`
            mt-6 w-full py-3 md:py-4 px-4 md:px-6 rounded-lg font-semibold transition-all duration-200
            flex items-center justify-center gap-3 text-sm md:text-base
            ${
              selectedFile && schoolName.trim() && !isUploading
                ? "bg-[#E3B505] hover:bg-[#E3B505]/90 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }
          `}
        >
          {isUploading ? (
            <>
              <Loader2 className="w-5 h-5 md:w-6 md:h-6 animate-spin" />
              <span>Import en cours...</span>
            </>
          ) : (
            "Soumettre"
          )}
        </button>
      </div>
    </div>
  );
};

export default FileUpload;
